import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import AnalyticsKpiCard from '../components/AnalyticsKpiCard';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

import {
  ArrowLeft,
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  Clock,
  Pulse,
  Robot,
  TrendUp,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
const AgentAnalytics = () => {
  const { agentId } = useParams();
  const { activeOrganization } = useOrganization();
  const navigate = useNavigate();
  
  // Debug logging
  console.log('[AgentAnalytics] agentId from params:', agentId);
  console.log('[AgentAnalytics] activeOrganization:', activeOrganization?.organization_id);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);
  
  // Data states
  const [agentData, setAgentData] = useState(null);
  const [trendsData, setTrendsData] = useState([]);

  const loadAgentAnalytics = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch agent-specific analytics
      const agentRes = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/agents/${agentId}?days=${dateRange}`,
        {},
        activeOrganization.organization_id
      );

      if (!agentRes.ok) {
        if (agentRes.status === 404) {
          throw new Error('Agent not found');
        }
        throw new Error('Failed to load agent analytics');
      }

      const agentJson = await agentRes.json();
      setAgentData(agentJson);

      // Fetch trends data for this agent
      const trendsRes = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/trends?agent_id=${agentId}&days=${dateRange}&grouping=day`,
        {},
        activeOrganization.organization_id
      );

      if (trendsRes.ok) {
        const trendsJson = await trendsRes.json();
        setTrendsData(trendsJson.trends || []);
      }

    } catch (err) {
      console.error('Error loading agent analytics:', err);
      setError(err.message || 'Failed to load agent analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [agentId, activeOrganization, dateRange]);

  useEffect(() => {
    if (activeOrganization && agentId) {
      loadAgentAnalytics();
    }
  }, [activeOrganization, agentId, loadAgentAnalytics]);

  const handleRefresh = () => {
    loadAgentAnalytics(true);
  };

  const formatNumber = (value) => {
    return value.toLocaleString();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
        </div>
      
    );
  }

  if (error) {
    return (
        <div className="max-w-4xl mx-auto px-6">
            <div className="glass rounded-2xl p-12 text-center">
              <WarningCircle size={64} className="mx-auto mb-6 text-[#EF4444]" />
              <h2 className="text-2xl font-bold text-white mb-4">Error Loading Analytics</h2>
              <p className="text-[#A0AEC0] mb-8">{error}</p>
              <button
                onClick={() => navigate('/analytics')}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold hover:opacity-90 transition-opacity"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
      
    );
  }

  return (
      <div className="max-w-7xl mx-auto px-6">
          {/* Back Button */}
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-2 text-[#6366F1] hover:text-[#4F46E5] mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-semibold">Back to Analytics Dashboard</span>
          </button>

          {/* Agent Header */}
          {agentData && (
            <div className="glass rounded-2xl p-6 mb-8">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                    <Robot size={32} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                      {agentData.agent_name || 'Agent'} Analytics
                    </h1>
                    {agentData.agent_role && (
                      <p className="text-[#A0AEC0] text-base">
                        Role: {agentData.agent_role}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Date Range Selector */}
                  <div className="flex gap-2">
                    {[7, 30, 90].map((days) => (
                      <button
                        key={days}
                        onClick={() => setDateRange(days)}
                        className={`px-4 py-2 rounded-full font-semibold transition-all ${
                          dateRange === days
                            ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
                            : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                        }`}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>

                  {/* ArrowsClockwise Button */}
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-all disabled:opacity-50"
                    title="Refresh data"
                  >
                    <ArrowsClockwise size={20} className={`text-[#6366F1] ${refreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* KPI Cards */}
          {agentData && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <AnalyticsKpiCard
                icon={Pulse}
                label="Total Calls"
                value={formatNumber(agentData.total_calls || 0)}
              />
              <AnalyticsKpiCard
                icon={CheckCircle}
                label="Success Rate"
                value={agentData.success_rate || 0}
                unit="%"
              />
              <AnalyticsKpiCard
                icon={Clock}
                label="Avg Response"
                value={Math.round(agentData.avg_response_time_ms || 0)}
                unit="ms"
              />
            </div>
          )}

          {/* Usage Trends Chart */}
          {trendsData.length > 0 && (
            <div className="glass rounded-2xl p-6 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <TrendUp size={24} className="text-[#6366F1]" />
                Usage Over Time
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    stroke="#A0AEC0"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#6366F1"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.95)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      padding: '12px'
                    }}
                    labelStyle={{ color: '#A0AEC0', marginBottom: '8px' }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="circle"
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="calls"
                    stroke="#6366F1"
                    strokeWidth={2}
                    dot={{ fill: '#6366F1', r: 4 }}
                    name="API Calls"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Performance Metrics */}
          <div className="grid lg:grid-cols-1 gap-8 mb-8">
            {/* Success vs Error Rate */}
            {agentData && (
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Pulse size={24} className="text-[#6366F1]" />
                  Performance Metrics
                </h2>
                
                {/* Success/Error Bar Chart */}
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={[
                      {
                        name: 'Calls',
                        Success: agentData.success_count || 0,
                        Errors: agentData.error_count || 0
                      }
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="name" stroke="#A0AEC0" />
                    <YAxis stroke="#A0AEC0" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(15, 23, 42, 0.95)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        padding: '12px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Success" fill="#14F195" />
                    <Bar dataKey="Errors" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>

                {/* Detailed Stats */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-xl glass">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} className="text-[#14F195]" />
                      <span className="text-[#A0AEC0]">Successful Calls</span>
                    </div>
                    <span className="text-white font-semibold">
                      {formatNumber(agentData.success_count || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl glass">
                    <div className="flex items-center gap-2">
                      <XCircle size={18} className="text-[#EF4444]" />
                      <span className="text-[#A0AEC0]">Failed Calls</span>
                    </div>
                    <span className="text-white font-semibold">
                      {formatNumber(agentData.error_count || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl glass">
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-[#6366F1]" />
                      <span className="text-[#A0AEC0]">Avg Response Time</span>
                    </div>
                    <span className="text-white font-semibold">
                      {Math.round(agentData.avg_response_time_ms || 0)}ms
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Empty State */}
          {agentData && agentData.total_calls === 0 && (
            <div className="glass rounded-2xl p-12 text-center">
              <Pulse size={64} className="mx-auto mb-6 text-[#A0AEC0]" />
              <h2 className="text-2xl font-bold text-white mb-4">No Usage Data Yet</h2>
              <p className="text-[#A0AEC0] mb-8">
                This agent has not been used yet. Start using it to see analytics here.
              </p>
              <button
                onClick={() => navigate(`/partner-pro/ai-workforce/agents/${agentId}`)}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold hover:opacity-90 transition-opacity"
              >
                View Agent Details
              </button>
            </div>
          )}
        </div>
  );
};

export default AgentAnalytics;
